import { forwardRef, useEffect, useId, useState } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { Plug, SquarePen } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@ludus/ui'
import type { Pipeline } from '@ludus/core'
import { createPipeline, PipelineApiError, type CreatePipelineInput } from '@/lib/api/pipelines.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'

interface NewPipelineDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
}

const newPipelineSchema = z.object({
  name: z.string().trim().min(1, 'required'),
  origin: z.enum(['native', 'external']),
})

type NewPipelineFormValues = z.infer<typeof newPipelineSchema>

export function NewPipelineDialog({ open, onOpenChange }: NewPipelineDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const formId = useId()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewPipelineFormValues>({
    resolver: zodResolver(newPipelineSchema),
    defaultValues: { name: '', origin: 'native' },
  })

  const [submitError, setSubmitError] = useState<PipelineApiError | null>(null)

  const { trigger, isMutating } = useSWRMutation(
    '/pipelines',
    (_key: string, { arg }: { arg: CreatePipelineInput }) => createPipeline(arg),
    {
      // The engine orders the catalogue by `createdAt` ascending.
      populateCache: (created: Pipeline, current: Pipeline[] | undefined) => [
        ...(current ?? []),
        created,
      ],
      revalidate: false,
    },
  )

  useEffect(() => {
    if (open) {
      reset({ name: '', origin: 'native' })
      setSubmitError(null)
    }
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    // The submit button is disabled while mutating; the Enter key is not.
    if (isMutating) return
    setSubmitError(null)
    try {
      const pipeline = await trigger({ name: values.name, origin: values.origin })
      onOpenChange(false)
      navigate(`/pipelines/${pipeline.id}/settings`)
    } catch (err) {
      setSubmitError(
        err instanceof PipelineApiError
          ? err
          : new PipelineApiError({ kind: 'unknown', status: null, message: 'submit failed' }),
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="@container max-w-lg">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>
              {t('pages.pipelines.newPipeline.title', { defaultValue: 'New pipeline' })}
            </DialogTitle>
            <DialogDescription>
              {t('pages.pipelines.newPipeline.description', {
                defaultValue: 'Pick where its deals come from — it cannot be changed later.',
              })}
            </DialogDescription>
          </div>
          <DialogClose />
        </DialogHeader>
        <DialogBody className="py-6">
          <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-6">
            <label className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.newPipeline.name', { defaultValue: 'Name' })}
              </span>
              <Input
                {...register('name')}
                autoFocus
                placeholder={t('pages.pipelines.newPipeline.namePlaceholder', {
                  defaultValue: 'e.g. Inbound sales',
                })}
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <span role="alert" className="text-[11px] font-medium text-toast-error-text">
                  {t('pages.pipelines.newPipeline.nameRequired', { defaultValue: 'Enter a name.' })}
                </span>
              )}
            </label>
            <fieldset className="flex flex-col">
              <legend className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
                {t('pages.pipelines.newPipeline.origin', { defaultValue: 'Type' })}
              </legend>
              <div className="grid grid-cols-1 gap-2.5 @sm:grid-cols-2">
                <OriginCard
                  {...register('origin')}
                  value="native"
                  icon={SquarePen}
                  title={t('pages.pipelines.newPipeline.originNative', {
                    defaultValue: 'Native',
                  })}
                  body={t('pages.pipelines.newPipeline.originNativeBody', {
                    defaultValue: 'Deals are created in Ludus, and you own the stages.',
                  })}
                />
                <OriginCard
                  {...register('origin')}
                  value="external"
                  icon={Plug}
                  title={t('pages.pipelines.newPipeline.originExternal', {
                    defaultValue: 'External',
                  })}
                  body={t('pages.pipelines.newPipeline.originExternalBody', {
                    defaultValue: 'Deals come from a connected source, which owns the stages.',
                  })}
                />
              </div>
            </fieldset>
            {submitError && (
              <p role="alert" className="text-[11px] font-medium text-toast-error-text">
                {apiErrorMessage(submitError, t, 'pages.pipelines.newPipeline')}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isMutating}
          >
            {t('pages.pipelines.newPipeline.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="submit" form={formId} variant="primary" size="sm" disabled={isMutating}>
            {t('pages.pipelines.newPipeline.create', { defaultValue: 'Create' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const OriginCard = forwardRef<
  HTMLInputElement,
  ComponentPropsWithoutRef<'input'> & { icon: LucideIcon; title: string; body: string }
>(function OriginCard({ icon: Icon, title, body, ...input }, ref) {
  return (
    <label className="cursor-pointer">
      <input {...input} ref={ref} type="radio" className="peer sr-only" />
      <span className="flex h-full flex-col gap-2.5 rounded-lg border border-border bg-background-secondary/40 px-4 py-3.5 transition-colors hover:border-border-hover peer-checked:border-primary/50 peer-checked:bg-primary/15 peer-checked:[&_[data-slot=icon]]:bg-primary-light peer-checked:[&_[data-slot=icon]]:text-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40">
        <span className="flex items-center gap-2.5">
          <span
            data-slot="icon"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background text-muted transition-colors"
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="text-sm font-medium text-foreground">{title}</span>
        </span>
        <span className="text-xs leading-relaxed text-muted">{body}</span>
      </span>
    </label>
  )
})
